import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import axios from 'axios';

import {
  DEFAULT_LOCALE,
  IS_WITH_LOCALIZATION,
  PUBLIC_URL,
} from '../../constants';

const LocalizationContext = createContext({
  locale: DEFAULT_LOCALE,
  localization: {},
  isLocalizationLoading: false,
  getLocalization: (text) => text,
  setLocale: () => {},
});

const filterStringValues = (obj) => Object.keys(obj)
  .filter((key) => typeof obj[key] === 'string')
  .reduce(
    (acc, key) => ({
      ...acc,
      [key]: obj[key],
    }),
    {},
  );

export const LocalizationProvider = ({ children }) => {
  const [isLocalizationLoading, setIsLocalizationLoading] = useState(false);
  const [locale, setLocale] = useState(DEFAULT_LOCALE);
  const [localization, setLocalization] = useState({});

  useEffect(() => {
    const fetchLocalizationData = async () => {
      setIsLocalizationLoading(true);
      try {
        const response = await axios.get(
          `${PUBLIC_URL}/localization/${locale}.json`,
        );
        setLocalization(filterStringValues(response.data));
      } catch (e) {
        console.error(e);
      } finally {
        setIsLocalizationLoading(false);
      }
    };

    if (IS_WITH_LOCALIZATION) {
      fetchLocalizationData();
    }
  }, [locale]);

  const getLocalization = useCallback(
    (text) => {
      if (!text || !localization) {
        return text;
      }

      const keys = Object.keys(localization);

      if (keys.length) {
        const lowerCaseText = text.toLowerCase();
        const translatedKey = keys.find(
          (k) => k.toLowerCase() === lowerCaseText,
        );

        if (process.env.NODE_ENV === 'development' && !translatedKey) {
          console.warn(`localization: "${text}" expression not found`);
        }

        if (translatedKey && localization[translatedKey]) {
          return localization[translatedKey];
        }
      }

      return text;
    },
    [localization],
  );

  return (
    <LocalizationContext.Provider
      value={{
        isLocalizationLoading,
        getLocalization,
        setLocale,
        locale,
        localization,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => useContext(LocalizationContext);
